/**
 * HOME FOR SUMMER – Build Your Set
 * Pulls products dynamically from Shopify collections
 */

(function () {
  'use strict';

  // Size tabs mapped to Shopify collection handles
  const SIZES = {
    beach: { label: 'Beach Towel', collection: 'beach-towels' },
    bath:  { label: 'Bath Towel',  collection: 'bath-towels' },
    hand:  { label: 'Hand Towel',  collection: 'hand-towels' },
  };

  // Discount tiers
  const TIERS = [
    { min: 6, discount: 0.18, label: '18% off' },
    { min: 4, discount: 0.12, label: '12% off' },
    { min: 2, discount: 0.05, label: '5% off' },
    { min: 0, discount: 0,    label: 'Full price' },
  ];

  const FREE_SHIPPING = 99;

  // State
  // cart keys: "variantId" => { qty, sizeKey, title, price, image, variantId }
  const cart = {};
  // Cache fetched products per size so we don't refetch
  const productCache = {};

  // DOM refs
  let sidebarItems, sidebarEmpty, sidebarSummary, sidebarCount;
  let subtotalEl, discountRow, discountPctEl, discountAmountEl, totalEl;
  let savingsEl, savingsAmountEl;
  let shippingFill, shippingAway, shippingFree;
  let checkoutBtn, nudgeBtn;
  let tierEls, mainEl;

  function init() {
    // Cache DOM
    mainEl = document.querySelector('.bys-page__main');
    sidebarItems = document.querySelector('.bys-sidebar__items');
    sidebarEmpty = document.querySelector('.bys-sidebar__empty');
    sidebarSummary = document.querySelector('.bys-sidebar__summary');
    sidebarCount = document.querySelector('.bys-sidebar__count');
    subtotalEl = document.querySelector('.bys-sidebar__subtotal');
    discountRow = document.querySelector('.bys-sidebar__row--discount');
    discountPctEl = document.querySelector('.bys-sidebar__discount-pct');
    discountAmountEl = document.querySelector('.bys-sidebar__discount-amount');
    totalEl = document.querySelector('.bys-sidebar__total');
    savingsEl = document.querySelector('.bys-sidebar__savings');
    savingsAmountEl = document.querySelector('.bys-sidebar__savings-amount');
    shippingFill = document.querySelector('.bys-sidebar__shipping-fill');
    shippingAway = document.querySelector('.bys-sidebar__shipping-away');
    shippingFree = document.querySelector('.bys-sidebar__shipping-free');
    checkoutBtn = document.querySelector('.bys-sidebar__checkout');
    nudgeBtn = document.querySelector('.bys-sidebar__nudge');
    tierEls = document.querySelectorAll('.bys-tiers__tier');

    // Remove hardcoded fallback grid
    const hardcoded = document.querySelector('.bys-grid--hardcoded');
    if (hardcoded) hardcoded.remove();

    // Load the first tab (beach)
    loadTab('beach');

    // Bind tab clicks
    document.querySelectorAll('.bys-tabs__tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const sizeKey = tab.dataset.tab;
        document.querySelectorAll('.bys-tabs__tab').forEach((t) =>
          t.classList.toggle('bys-tabs__tab--active', t.dataset.tab === sizeKey)
        );
        loadTab(sizeKey);
      });
    });

    // Bind quantity buttons (delegated)
    mainEl.addEventListener('click', (e) => {
      // Handle ADD button
      const addBtn = e.target.closest('.bys-card__btn-add');
      if (addBtn) {
        const card = addBtn.closest('.bys-card');
        const vid = card.dataset.variantId;
        if (!cart[vid]) {
          cart[vid] = makeCartEntry(card, 0);
        }
        cart[vid].qty += 1;
        updateCardDisplay(card, vid);
        updateSidebar();
        return;
      }

      const btn = e.target.closest('.bys-card__btn');
      if (!btn) return;

      const card = btn.closest('.bys-card');
      const vid = card.dataset.variantId;
      const isPlus = btn.classList.contains('bys-card__btn--plus');

      if (isPlus) {
        if (!cart[vid]) cart[vid] = makeCartEntry(card, 0);
        cart[vid].qty += 1;
      } else {
        if (cart[vid]) {
          cart[vid].qty = Math.max(0, cart[vid].qty - 1);
          if (cart[vid].qty === 0) delete cart[vid];
        }
      }

      updateCardDisplay(card, vid);
      updateSidebar();
    });

    // Nudge banner – scrolls to product grid
    if (nudgeBtn) {
      nudgeBtn.addEventListener('click', () => {
        document.querySelector('.bys-tabs').scrollIntoView({ behavior: 'smooth' });
      });
    }

    // Checkout button
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', addToShopifyCart);
    }
  }

  function makeCartEntry(card, qty) {
    return {
      qty: qty,
      sizeKey: card.dataset.size,
      title: card.dataset.title,
      price: parseFloat(card.dataset.price),
      image: card.dataset.image,
      variantId: card.dataset.variantId,
    };
  }

  // Fetch products from a Shopify collection and render the grid
  async function loadTab(sizeKey) {
    // Hide all grids
    mainEl.querySelectorAll('.bys-grid').forEach((g) => (g.style.display = 'none'));

    // Check if grid already exists
    const existing = mainEl.querySelector('.bys-grid[data-size="' + sizeKey + '"]');
    if (existing) {
      existing.style.display = '';
      return;
    }

    const size = SIZES[sizeKey];
    if (!size) return;

    // Show loading state
    const loader = document.createElement('div');
    loader.className = 'bys-grid bys-grid--loading';
    loader.dataset.size = sizeKey;
    loader.innerHTML = '<p class="bys-loading">Loading products...</p>';
    mainEl.appendChild(loader);

    try {
      let products = productCache[sizeKey];
      if (!products) {
        const res = await fetch('/collections/' + size.collection + '/products.json?limit=20');
        if (!res.ok) throw new Error('Collection not found');
        const data = await res.json();
        products = data.products;
        productCache[sizeKey] = products;
      }

      // Remove loader
      loader.remove();

      // Build grid
      const grid = document.createElement('div');
      grid.className = 'bys-grid';
      grid.dataset.size = sizeKey;

      if (products.length === 0) {
        grid.innerHTML = '<p class="bys-loading">No products found in this collection yet.</p>';
        mainEl.appendChild(grid);
        return;
      }

      products.forEach((product) => {
        const variant = product.variants[0];
        const price = parseFloat(variant.price);
        const image = product.images[0]
          ? product.images[0].src
          : '';
        const variantId = variant.id;

        const card = document.createElement('div');
        card.className = 'bys-card';
        card.dataset.size = sizeKey;
        card.dataset.variantId = variantId;
        card.dataset.price = price;
        card.dataset.title = product.title;
        card.dataset.image = image;

        const imgHtml = image
          ? '<img src="' + image.replace(/\.([a-z]+)$/i, '_600x.$1') + '" alt="' + product.title + '" loading="lazy">'
          : '<div class="bys-card__placeholder"><span>' + size.label + '</span></div>';

        const qty = cart[variantId] ? cart[variantId].qty : 0;
        const activeClass = qty > 0 ? ' bys-card--active' : '';

        card.className = 'bys-card' + activeClass;
        card.innerHTML =
          '<div class="bys-card__image">' + imgHtml + '</div>' +
          '<div class="bys-card__info">' +
            '<h3 class="bys-card__name">' + product.title + '</h3>' +
            '<p class="bys-card__size">' + size.label + '</p>' +
            '<p class="bys-card__price">$' + price + '</p>' +
          '</div>' +
          '<div class="bys-card__qty">' +
            '<button class="bys-card__btn-add" aria-label="Add to set">ADD</button>' +
            '<div class="bys-card__qty-controls">' +
              '<button class="bys-card__btn bys-card__btn--minus" aria-label="Remove one">\u2212</button>' +
              '<span class="bys-card__count">' + qty + '</span>' +
              '<button class="bys-card__btn bys-card__btn--plus" aria-label="Add one">+</button>' +
            '</div>' +
          '</div>';

        grid.appendChild(card);
      });

      mainEl.appendChild(grid);
    } catch (err) {
      console.error('Failed to load collection:', sizeKey, err);
      loader.innerHTML = '<p class="bys-loading">Could not load products. Please refresh.</p>';
    }
  }

  function updateCardDisplay(card, vid) {
    const qty = cart[vid] ? cart[vid].qty : 0;
    card.querySelector('.bys-card__count').textContent = qty;
    card.classList.toggle('bys-card--active', qty > 0);
  }

  function getTier(count) {
    for (const tier of TIERS) {
      if (count >= tier.min) return tier;
    }
    return TIERS[TIERS.length - 1];
  }

  function getNudgeMessage(totalItems, subtotal) {
    switch (totalItems) {
      case 0:
        return '';
      case 1:
        return 'Add 1 more to unlock <strong>5% off</strong> your entire set';
      case 2: {
        var handPrice = (27 * 0.95).toFixed(2);
        return 'Add a hand towel to complete the set \u2013 just <strong>$' + handPrice + '</strong> with your discount';
      }
      case 3: {
        var extraSavings = (subtotal * 0.07).toFixed(2);
        return 'Just 1 more to jump to <strong>12% off</strong> \u2013 save an extra <strong>$' + extraSavings + '</strong>';
      }
      case 4:
        return 'Most customers add a 5th \u2013 you\'re 2 away from <strong>18% off</strong>';
      case 5:
        return 'Just 1 more to unlock our biggest discount \u2013 <strong>18% off everything</strong>';
      default:
        return 'Maximum savings unlocked! Every towel you add saves you <strong>18%</strong>';
    }
  }

  function updateSidebar() {
    const entries = Object.entries(cart).filter(([, v]) => v.qty > 0);
    const totalItems = entries.reduce((sum, [, v]) => sum + v.qty, 0);
    const tier = getTier(totalItems);

    let subtotal = 0;
    entries.forEach(([, v]) => {
      subtotal += v.price * v.qty;
    });

    const discountAmount = subtotal * tier.discount;
    const total = subtotal - discountAmount;

    // Update tier indicators
    tierEls.forEach((el) => {
      const min = parseInt(el.dataset.min);
      el.classList.toggle('bys-tiers__tier--active', totalItems >= min);
      el.classList.toggle('bys-tiers__tier--current', getTier(totalItems).min === min);
    });

    sidebarCount.textContent = totalItems + (totalItems === 1 ? ' item' : ' items');

    if (totalItems === 0) {
      sidebarEmpty.style.display = '';
      sidebarSummary.style.display = 'none';
      nudgeBtn.style.display = 'none';
      checkoutBtn.disabled = true;
      renderSidebarItems([]);
      return;
    }

    sidebarEmpty.style.display = 'none';
    sidebarSummary.style.display = '';
    checkoutBtn.disabled = false;

    renderSidebarItems(entries);

    subtotalEl.textContent = '$' + subtotal.toFixed(2);

    if (tier.discount > 0) {
      discountRow.style.display = '';
      discountPctEl.textContent = Math.round(tier.discount * 100);
      discountAmountEl.textContent = '\u2212$' + discountAmount.toFixed(2);
      savingsEl.style.display = '';
      savingsAmountEl.textContent = '$' + discountAmount.toFixed(2);
    } else {
      discountRow.style.display = 'none';
      savingsEl.style.display = 'none';
    }

    totalEl.textContent = '$' + total.toFixed(2);

    // Shipping progress
    const shippingPct = Math.min(100, (total / FREE_SHIPPING) * 100);
    shippingFill.style.width = shippingPct + '%';

    if (total >= FREE_SHIPPING) {
      shippingAway.style.display = 'none';
      shippingFree.style.display = '';
      shippingFill.classList.add('bys-sidebar__shipping-fill--complete');
    } else {
      const away = (FREE_SHIPPING - total).toFixed(0);
      shippingAway.style.display = '';
      shippingAway.textContent = '$' + away + ' away from free shipping';
      shippingFree.style.display = 'none';
      shippingFill.classList.remove('bys-sidebar__shipping-fill--complete');
    }

    // Nudge banner
    var nudgeMsg = getNudgeMessage(totalItems, subtotal);
    if (nudgeMsg) {
      nudgeBtn.innerHTML = nudgeMsg;
      nudgeBtn.style.display = '';
    } else {
      nudgeBtn.style.display = 'none';
    }
  }

  function renderSidebarItems(entries) {
    const existing = sidebarItems.querySelectorAll('.bys-sidebar__item');
    existing.forEach((el) => el.remove());

    entries.forEach(([, v]) => {
      const item = document.createElement('div');
      item.className = 'bys-sidebar__item';

      const imgHtml = v.image
        ? '<img class="bys-sidebar__item-img" src="' + v.image.replace(/\.([a-z]+)$/i, '_80x.$1') + '" alt="' + v.title + '">'
        : '<div class="bys-sidebar__item-img bys-sidebar__item-img--placeholder"></div>';

      item.innerHTML =
        imgHtml +
        '<div class="bys-sidebar__item-details">' +
          '<span class="bys-sidebar__item-name">' + v.qty + '\u00d7 ' + v.title + '</span>' +
          '<span class="bys-sidebar__item-color">' + SIZES[v.sizeKey].label + '</span>' +
        '</div>' +
        '<span class="bys-sidebar__item-price">$' + (v.price * v.qty).toFixed(2) + '</span>';
      sidebarItems.appendChild(item);
    });
  }

  async function addToShopifyCart() {
    const entries = Object.entries(cart).filter(([, v]) => v.qty > 0);
    if (entries.length === 0) return;

    checkoutBtn.disabled = true;
    checkoutBtn.textContent = 'Adding...';

    try {
      const items = entries.map(([vid, v]) => ({
        id: parseInt(vid),
        quantity: v.qty,
        properties: { _builder: 'true' },
      }));

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      });

      checkoutBtn.textContent = 'Added!';
      setTimeout(() => {
        checkoutBtn.textContent = 'Add to Cart';
        checkoutBtn.disabled = false;
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          window.location.href = '/cart';
        }
      }, 800);
    } catch (err) {
      console.error('Cart add error:', err);
      checkoutBtn.textContent = 'Add to Cart';
      checkoutBtn.disabled = false;
    }
  }

  // Init when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
